import { IDropdownOption, Stack, Dropdown, IStackTokens, ResponsiveMode, TextField, PrimaryButton, IButtonStyles, Label, FontSizes, FontWeights, ILabelStyles, mergeStyles, Spinner, SpinnerSize, FontIcon, DatePicker, IconButton } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { IFile } from "../../../../../interfaces/IFile";
import { IFilePanelInfo } from "../../../../../interfaces/IFilePanelInfo";
import { isMobile, mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { useLazyGetDriveInfoFromSharepointIdQuery } from "../../../redux/api/graph/api-graph-slice";
import { IGraphCreateShareableLinkRequest, ShareableLinkRequestScope, ShareableLinkRequestType } from "../../../redux/api/graph/api-graph-interfaces";
import { RootState } from "../../../redux/storeMain";
import { ICustomPersonaProps, PeopleFinder } from "../../../_shared/PeopleFinder/PeopleFinder";
import { Fileview } from "../../Explorer/Fileview/Fileview";
import { ApprovalRequest, ItemLink, HookType, IHookResult } from "../../../redux/api/local/api-local-interfaces";
import { SuccessErrorMessage } from "../../../_shared/SuccessErrorMessage/SuccessErrorMessage";
import { createItemShareableLink, getFileItemIdFromRelativePath, getFileItemInfo, getItem, getItemApprovalRequestInfo, getItemWebViewUrl } from "../../../../../services/GraphService";
import { IDriveInfo } from "../../../../../interfaces/IDriveInfo";
import authModule from "../../../../../../../utilities/AuthModule";
import { ComponentTypes } from "../../../../../config/config";
import { apiGetHooksByType, apiRequestApproval } from "../../../../../services/ApiService";
import { SharePointFolderContentTypeIds, sharePointInternalFileName } from "../../../../../config/constants";
import { getValidHooks } from "../../../../../services/HooksService";
import { DateService } from "../../../../../services/dateService";
import { DayPickerStrings, firstDayOfWeek } from "../../../../../config/dateSettings";
import dayjs from "dayjs";
import { clearButtonClass } from "../../../../../config/controlStyles";
import { clearButtonProps, datePickerStyles, iconButtonProps, iconButtonStyles, textFieldStyles } from "../../../../../styles/datePickerStyles";

const labelInfoStyle: Partial<ILabelStyles> = {
  root: {
    fontWeight: FontWeights.regular,
    fontSize: FontSizes.medium,
    marginTop: 0,
  },
};

const panelActionButtonStyle: Partial<IButtonStyles> = {
  root: {
    marginTop: 20,
  },
};

interface PeoplePickerAddress {
  email: string;
  isCustomEmail?: boolean;
}

export interface IFileApproval extends IFile {
  itemId?: string
  folderId?: string;
  serverRelativePath?: string;
}

interface IAutomationPanelProps {
  file?: IFilePanelInfo;
  files?: IFileApproval[];
  isGraphQuery?: boolean;
  tenantEnablePII?: boolean;
  tenantGuid?: string;
  requestor?: string;
  isContentManager?: boolean;
  closeClicked: () => void;
  listName?: string;
  // Directory-shaped location used ONLY for hook whitelist/blacklist validation.
  // Callers whose files do not live in the ambient working directory (the
  // cross-library aggregate view) pass the files' own library here; without it,
  // hook library restrictions would be validated against the wrong location.
  hookValidationLocation?: any;
}

const stackTokens: IStackTokens = { childrenGap: 20 };

const minItems = 5;

export const AutomationPanel = ({ file, files, tenantEnablePII = false, tenantGuid, requestor, isContentManager, closeClicked, listName, hookValidationLocation }: IAutomationPanelProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const user = useSelector((state: RootState) => state.user);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);

  const [getDriveInfoFromSharepointId] = useLazyGetDriveInfoFromSharepointIdQuery();

  const [fileViews, setFileViews] = React.useState<JSX.Element[]>([]);
  const [isBusySending, setIsBusySending] = React.useState(false);
  const [formDisabled, setFormDisabled] = React.useState(true);
  const [otherError, setOtherError] = React.useState('');
  const [hooks, setHooks] = React.useState<IHookResult[]>([]);
  const [selectedFlow, setSelectedFlow] = React.useState<IHookResult>();
  const [isSuccessRequestApproval, setIsSuccessRequestApproval] = React.useState(false);
  const [isErrorRequestApproval, setIsErrorRequestApproval] = React.useState(false);
  const [errorRequestApproval, setErrorRequestApproval] = React.useState();
  const [options, setOptions] = React.useState<IDropdownOption[]>();
  const [approvalFiles, setApprovalFiles] = React.useState<IFileApproval[]>();
  const [additionalDetails, setAdditionalDetails] = React.useState<string>('');
  const [dueDate, setDueDate] = React.useState<Date>();
  const [dateFormat] = React.useState<Intl.DateTimeFormat>(new Intl.DateTimeFormat());
  const [selectedApprovers, setSelectedApprovers] = React.useState<PeoplePickerAddress[]>([]);
  const [showMoreIsClicked, setShowMore] = React.useState<boolean>(false);
  const [sectionStyle, setSectionStyle] = React.useState<any>();

  const isInOneDrive = React.useRef(currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder);

  React.useEffect(() => {
    (async () => {
      const hooks = await apiGetHooksByType({
        TenantGUID: tenantGuid ?? user.account.tenantId,
        HookTypeId: HookType.AllApproval
      });

      const validHooks = getValidHooks(hooks, hookValidationLocation ?? currentDirectory);

      setHooks(validHooks);

      setSelectedFlow(validHooks.length === 1 ? validHooks[0] : null);

      setOptions(validHooks.map(hook => {
        return {
          key: hook.id,
          text: hook.name
        }
      }));
    })();
  }, []);

  React.useEffect(() => {
    if (approvalFiles) {
      setFileViews(
        approvalFiles?.map((item, index) => (
          <div key={index}>
            <Fileview
              item={item}
              isMobile={isMobile}
              multiSelect={false}
              hasShowMore={false}
              tenantEnablePII={tenantEnablePII}
              nonClickable={true}
            />
          </div>
        ))
      );
    }
  }, [approvalFiles]);

  React.useEffect(() => {
    if (file) {
      let tempFiles: IFileApproval[] = [];
      tempFiles.push({
        ...file
      })
      setApprovalFiles(tempFiles);
    }
    else {
      setApprovalFiles(files);
    }
  }, []);

  React.useEffect(() => {
    let isDisabled = false;
    if (!selectedFlow) {
      isDisabled = true;
    } else if ((selectedFlow.typeId !== HookType.Generic || selectedFlow.isPeoplePickerRequired) && selectedApprovers?.length === 0) {
      isDisabled = true;
    }
    setFormDisabled(isDisabled);
  }, [selectedFlow, selectedApprovers]);

  React.useEffect(() => {
    const itemHeight = isMobile ? mobileListItemStyle.height : 40;
    let containerHeight = itemHeight * approvalFiles?.length;

    if (!showMoreIsClicked) {
      containerHeight = approvalFiles?.length > minItems ? itemHeight * minItems : itemHeight * approvalFiles?.length;
    }

    setSectionStyle(
      mergeStyles({
        maxHeight: containerHeight,
        overflow: "hidden",
        transition: "max-height 0.5s ease-in-out",
      })
    );
  }, [showMoreIsClicked, approvalFiles]);

  const selectedPersonasChanged = (selectedPersonas: ICustomPersonaProps[]) => {
    setSelectedApprovers(selectedPersonas.map(x => {
      return {
        email: x.secondaryText,
        isCustomEmail: x.isCustomEmail
      }
    }));
  }

  const sendClicked = async () => {
    setIsBusySending(true);
    setOtherError('');
    setErrorRequestApproval(null);
    setIsErrorRequestApproval(false);
    setIsSuccessRequestApproval(false);

    const tempFiles = [...approvalFiles];

    const links: ItemLink[] = [];
    let hasError = false;
    const result = await Promise.all(
      tempFiles.map(async tempFile => {
        const itemContentTypeId: string = currentDirectory?.type === ComponentTypes.SharePointList
          ? tempFile["ContentTypeId"]
          : "";

        if (itemContentTypeId?.startsWith(SharePointFolderContentTypeIds.item)) {
          const itemlink: ItemLink = {
            name: tempFile["Title"] || "",
            uniqueId: tempFile["UniqueId"].replace("{", "").replace("}", ""),
            siteUrl: currentDirectory.webUrl,
            listId: currentDirectory.sharePointListId,
            listName: currentDirectory.sharepointList,
            serverRelativeUrl: tempFile["FileRef"],
            itemId: tempFile["ID"]
          }
          links.push(itemlink);
          Promise.resolve(itemlink);
        }
        else {
          let itemId = "";
          let driveId = "";

          if (!isContentManager) {
            driveId = tempFile.driveId;
            if (tempFile.id && !tempFile.itemId && !tempFile.hasGraphItemId) {

              const driveInfoResults = await getDriveInfoFromSharepointId({ request: { driveId, sharepointId: tempFile.id }, accessToken: await authModule.getGraphAccessToken() }).unwrap();

              if (driveInfoResults.value.length > 0) {
                itemId = driveInfoResults.value[0].id;
              }
              else {
                const driveInfo: IDriveInfo = !tempFile.folderId ? { id: driveId, driveId: null } : { id: tempFile.folderId, driveId }

                const fileItem = await getFileItemInfo(driveInfo, tempFile.name, "id,webUrl");
                if (fileItem) {
                  itemId = fileItem.id;
                }
              }
            }
            else if (tempFile.hasGraphItemId) {
              itemId = tempFile?.id;
            }
            else {
              //source: Advanced Search
              itemId = tempFile.itemId;
              if (!itemId) {
                itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, tempFile["serverRelativePath"]);
              }
            }
          } else {
            driveId = isInOneDrive.current ? oneDriveRootDetails?.driveId : (currentDirectory?.parentId ? currentDirectory?.parentId : currentDirectory?.id);

            const spItemUrlparts = tempFile['.spItemUrl'].split('/');
            itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];

            if (!itemId) {
              const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
              const folderUrl = new URL(driveUrl.origin + tempFile['FileRef']);
              itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
            }
          }

          if (itemId) {
            const request: IGraphCreateShareableLinkRequest = {
              driveId,
              itemId,
              recipients: selectedApprovers.map(x => x.email),
              type: ShareableLinkRequestType.View,
              scope: ShareableLinkRequestScope.Users
            }

            try {
              let [createLinkResults, fileDetails] = await Promise.all([createItemShareableLink(request), getItemApprovalRequestInfo(driveId, itemId)]);

              if (createLinkResults === null) {
                // Create sharing links for internal users only. The flow implementation has to accommodate external users
                createLinkResults = await createItemShareableLink({
                  ...request,
                  recipients: selectedApprovers.filter(x => !x.isCustomEmail).map(x => x.email)
                });
              }

              const link: ItemLink = {
                name: tempFile.name || tempFile["Name"] || tempFile[sharePointInternalFileName] || "",
                sharingUrl: createLinkResults?.link?.webUrl ?? fileDetails?.listItem?.webUrl,
                fullWebUrl: fileDetails?.listItem?.webUrl,
                uniqueId: fileDetails?.sharepointIds?.listItemUniqueId,
                siteUrl: fileDetails?.sharepointIds?.siteUrl,
                listId: fileDetails?.sharepointIds?.listId,
                listName: listName || currentDirectory?.DocumentLibrary || (await getItem(await authModule.getGraphAccessToken(), driveId, null))?.name,
                serverRelativeUrl: tempFile["FileRef"],
                itemId: tempFile["ID"] || fileDetails?.sharepointIds?.listItemId
              }

              links.push(link);

              Promise.resolve(link);
            } catch (error) {
              hasError = true;
              setOtherError(languageStrings.AutomationFlowRequestError);
              Promise.reject(languageStrings.AutomationFlowRequestError);
            }
          }
        }
      })
    );

    if (result && !hasError) {
      const approvalRequest: ApprovalRequest = {
        hookId: selectedFlow.id,
        hookTypeId: selectedFlow.typeId,
        hookName: selectedFlow.name,
        tenantGuid: tenantGuid ? tenantGuid : user.account.tenantId,
        accountId: user.account.accountId,
        requestor: requestor ? requestor : user.account.username,
        approvers: selectedApprovers.map(x => x.email),
        dueDate: dueDate ?? (selectedFlow.dueDateDays > 0 ? dayjs().add(selectedFlow.dueDateDays, "day").toDate() : null),
        details: additionalDetails,
        itemLinks: links
      }

      try {
        await apiRequestApproval(approvalRequest);
        setIsSuccessRequestApproval(true);
        setIsBusySending(false);
      } catch (error) {
        setIsErrorRequestApproval(true);
        setIsBusySending(false);
        error?.response ? setErrorRequestApproval(error?.response) : setOtherError(languageStrings.AutomationFlowRequestError);
      }

    } else {
      setIsBusySending(false);
    }
  }

  const flowChanged = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    setSelectedFlow(hooks.find(x => x.id === item.key));
  }

  const onFormatDate = React.useCallback((date?: Date): string => {
    return !date || date?.getTime() !== date?.getTime() ? "" : dateFormat.format(date);
  }, [dateFormat]);

  const onClearClick = (ev: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (!ev.defaultPrevented) {
      setDueDate(undefined);
      ev.stopPropagation();
      ev.preventDefault();
    }
  };

  const showMore = approvalFiles?.length > minItems && (
    <div
      className="xb-show-more-action"
      onClick={() => setShowMore((prevShowMoreIsClicked) => !prevShowMoreIsClicked)}
      style={{ marginTop: "5px" }}
    >
      <FontIcon iconName={!showMoreIsClicked ? "ChevronDown" : "ChevronUp"} />
      <span>{!showMoreIsClicked ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
    </div>
  );

  return (
    <>
      <div style={{ marginLeft: -15, marginRight: -15 }}>
        <div className={sectionStyle} style={{ paddingTop: "15px" }}>
          {fileViews}
        </div>
      </div>
      {showMore}
      <Stack tokens={stackTokens} style={{ marginTop: '15px' }}>
        <Dropdown
          disabled={isSuccessRequestApproval || isBusySending}
          required
          placeholder={languageStrings.SelectOption}
          label={languageStrings.AutomationFlowName}
          options={options}
          className="xb-sp-input"
          // responsiveMode={ResponsiveMode.small}
          selectedKey={options && options.length === 1 ? options[0].key : selectedFlow?.id}
          onChange={(event, item) => flowChanged(event, item)}
        />

        <Stack.Item>
          <Label>{languageStrings.AutomationFlowDescription}</Label>
          <Label
            styles={labelInfoStyle}
            disabled
          >{selectedFlow?.description ? selectedFlow.description : languageStrings.AutomationFlowNotSelected}</Label>
        </Stack.Item>

        {(selectedFlow && (selectedFlow.typeId !== HookType.Generic || selectedFlow.isPeoplePickerRequired))
          && <PeopleFinder
            allowExternalUsers={selectedFlow.typeId === HookType.Generic}
            disabled={isSuccessRequestApproval || isBusySending}
            label={selectedFlow.typeId !== HookType.Generic
              ? (selectedFlow.peoplePickerTitle?.trim() || languageStrings.Approvers)
              : (!selectedFlow.peoplePickerTitle?.trim() || selectedFlow.peoplePickerTitle === "Participants" ? languageStrings.Participants : selectedFlow.peoplePickerTitle)}
            itemLimit={selectedFlow.peoplePickerItemLimit}
            selectedPersonasChanged={selectedPersonasChanged} />}

        {Number.isInteger(selectedFlow?.dueDateDays) &&
          <DatePicker
            label={languageStrings.DueDate}
            disabled={isSuccessRequestApproval || isBusySending}
            allowTextInput
            parseDateFromString={DateService.parseDateFromString}
            firstDayOfWeek={firstDayOfWeek}
            strings={DayPickerStrings}
            // value={selectedFlow?.dueDateDays && selectedFlow.dueDateDays > 0
            //   ? dayjs().add(selectedFlow.dueDateDays, "day").toDate()
            //   : undefined}
            minDate={dayjs().add(1, "day").toDate()}
            placeholder={selectedFlow.dueDateDays > 0 ? languageStrings.DueDateDescription.replace("{days}", selectedFlow.dueDateDays.toString()) : languageStrings.EnterDate}
            ariaLabel={languageStrings.DueDate}
            showMonthPickerAsOverlay={true}
            onSelectDate={setDueDate}
            formatDate={onFormatDate}
            textField={{
              onRenderSuffix: () => {
                return (
                  <div className={clearButtonClass}>
                    <IconButton
                      styles={iconButtonStyles}
                      iconProps={iconButtonProps}
                      {...clearButtonProps}
                      onClick={onClearClick}
                    />
                  </div>)
              },
              styles: textFieldStyles
            }}
            styles={datePickerStyles}
          />}

        <TextField disabled={isSuccessRequestApproval || isBusySending} label={languageStrings.Details} multiline rows={3} onChange={(event, newValue) => setAdditionalDetails(newValue)} />

        {(isSuccessRequestApproval || isErrorRequestApproval || otherError) && <Stack.Item>
          <SuccessErrorMessage
            isSuccess={isSuccessRequestApproval}
            isError={isErrorRequestApproval || otherError?.length > 1}
            successMessage={languageStrings.AutomationFlowRequestSuccess}
            errorMessage={otherError?.length > 1 ? otherError : languageStrings.AutomationFlowRequestError}
            errorList={errorRequestApproval ? (errorRequestApproval as any)?.data?.failedFileNames : null}
          />
        </Stack.Item>}

        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
          {!isSuccessRequestApproval && <PrimaryButton
            text={languageStrings.Send}
            styles={panelActionButtonStyle}
            iconProps={{ iconName: 'send' }}
            onClick={() => sendClicked()}
            disabled={isBusySending || formDisabled}
          >
            {isBusySending && <Spinner size={SpinnerSize.small} />}
          </PrimaryButton>}
          {isSuccessRequestApproval && <PrimaryButton
            text={languageStrings.Close}
            styles={panelActionButtonStyle}
            iconProps={{ iconName: 'Checkmark' }}
            onClick={() => closeClicked()}
          />}
        </Stack>
      </Stack>
    </>
  )
};